import { useQueryClient } from "@tanstack/react-query";
import { useDeleteWithUndoController, useNotify, useUpdate } from "ra-core";
import { useEffect } from "react";

import type { Task, TaskStatus } from "../types";
import { taskStatusUpdate } from "./taskStatuses";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Status changes, postponing and deletion of a task, shared by every task row. */
export const useTaskActions = (task: Task) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [update, { isPending, isSuccess, variables }] = useUpdate();
  const { handleDelete } = useDeleteWithUndoController({
    record: task,
    redirect: false,
    mutationOptions: {
      onSuccess() {
        notify("resources.tasks.deleted", { undoable: true });
      },
    },
  });

  useEffect(() => {
    // Checking a task keeps it in place; other changes (postpone) re-sort the lists.
    if (isPending || !isSuccess || variables?.data?.done_date != undefined) {
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["tasks", "getList"] });
  }, [queryClient, isPending, isSuccess, variables]);

  const isAbandoned = task.status === "abandoned";
  const isClosed = !!task.done_date;

  const setStatus = (status: TaskStatus) =>
    update("tasks", {
      id: task.id,
      data: taskStatusUpdate(status),
      previousData: task,
    });

  // Done at once on screen; the save waits for the notification, whose
  // "Undo" button cancels it. The notification is sent from onSuccess: the
  // undoable save is only queued after an await, and a notification sent
  // earlier would find no save to commit or cancel.
  const markDone = () => {
    update(
      "tasks",
      { id: task.id, data: taskStatusUpdate("done"), previousData: task },
      {
        mutationMode: "undoable",
        onSuccess: () =>
          notify("resources.tasks.marked_done", {
            type: "info",
            undoable: true,
          }),
      },
    );
  };

  const postpone = (days: number) =>
    update("tasks", {
      id: task.id,
      data: {
        due_date: new Date(Date.now() + days * DAY_MS)
          .toISOString()
          .slice(0, 10),
      },
      previousData: task,
    });

  return {
    isAbandoned,
    isClosed,
    isDone: isClosed && !isAbandoned,
    isPending,
    /** Round check: done <-> to do (an abandoned task becomes done). */
    toggleDone: () =>
      isClosed && !isAbandoned ? setStatus("todo") : markDone(),
    setStatus,
    postpone,
    handleDelete,
  };
};
